import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useFireAlignment, useCmsSubmissionPoll, useCmsSubmissions, useCaptureCmsBaseline } from '../state/data/cms';
import { usePatientIntakes } from '@/state/data/patientIntake';
import { CmsResultCodes, freshTerminalRow } from '@/logic/cmsResultCodes';
import { deriveEligibility } from '@/logic/eligibility';
import { buildAlignmentPacket, validateConditionInput, conditionSuggestions } from '@/logic/alignmentConditions';
import { scheduleInvalidate } from '@/state/data/_shared/wf-race';
import { TASK_KEYS } from '@/state/data/task/keys';
import { CMS_KEYS } from '@/state/data/cms/keys';
import { CARE_PERIOD_KEYS } from '@/state/data/carePeriod/keys';
import { LoadingPanel, VerdictBanner, Section, Callout } from './cms/cmsModalParts';

// Review the exact CMS $align payload, edit ICD-10 conditions, sign a typed-name
// attestation, then fire cms_alignment_requested (WF5 → WF12 $align).

// Alignment submit is fire-and-poll: the verdict lands on the cms_submissions
// row (operation='align'), not the event response. Cap mirrors eligibility's 30s.
const POLL_CAP_MS = 30_000;
const EMPTY_IDS = new Set();

const SUBMIT_LOADING_STEPS = [
  { at: 0,    label: 'Authenticating with CMS' },
  { at: 1500, label: 'Submitting to /Patient/$align' },
  { at: 6000, label: 'Reviewing CMS response' },
];

// Negative verdicts the operator can fix by editing the packet and resubmitting
// (vs. a terminal dead-end where the only action is Close).
const FIXABLE_ALIGN_CODES = new Set([
  'not-aligned-no-switch-attestation',
  'not-aligned-mismatch',
  'validation-failed',
]);

const DEFAULT_ATTESTATION =
  'I attest that the information in this alignment packet is accurate and '
  + 'that this patient meets CMS ACCESS Model eCKM eligibility requirements.';

const TABS = [
  { id: 'demographics', label: 'Demographics' },
  { id: 'conditions',   label: 'Conditions' },
  { id: 'consent',      label: 'Consent' },
  { id: 'eligibility',  label: 'Eligibility' },
  { id: 'attestation',  label: 'Attestation' },
  { id: 'fhir-preview', label: 'FHIR preview' },
];

/* ── ACCESS Track CodeSystem → display + code ──────────────────────── */
const TRACK_INFO = {
  eckm: { code: 'eCKM', display: 'Early Cardio-Kidney-Metabolic' },
  ckm:  { code: 'CKM',  display: 'Cardio-Kidney-Metabolic' },
  msk:  { code: 'MSK',  display: 'Musculoskeletal' },
  bh:   { code: 'BH',   display: 'Behavioral Health' },
};

/* buildAlignmentFhirParameters — mirrors WF12's "Build FHIR Alignment Params"
   jsCode so the back-office can render exactly what WF12 would POST to
   /Patient/$align without firing the workflow. Inputs are the same fields
   AlignmentPacketModal already has in hand. */
// Withings birthdate is Unix epoch SECONDS; CMS $align (FHIR Patient.birthDate)
// and WF12's dobValid both require YYYY-MM-DD. Convert, never transmit raw.
function toIsoBirthdate(v) {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10); // already ISO
  if (/^-?\d+$/.test(s)) {                                 // epoch seconds
    const d = new Date(Number(s) * 1000);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

// Map a raw Withings/member sex value → a FHIR-valid gender enum. CMS $align
// rejects anything outside male|female|other|unknown, so never pass sex raw.
function toFhirGender(sex) {
  const s = String(sex ?? '').trim().toLowerCase();
  if (['male', 'm', '0'].includes(s)) return 'male';
  if (['female', 'f', '1'].includes(s)) return 'female';
  if (s === 'other') return 'other';
  return 'unknown';
}

function buildAlignmentFhirParameters({ mbi, firstName, lastName, dob, sex, participantId, payerId, trackCode, conditions, isProviderReferral, reviewedBy, reviewedByRole, attestationText, attestationSignature }) {
  const track = TRACK_INFO[(trackCode || 'eckm').toLowerCase()] || TRACK_INFO.eckm;
  const params = {
    resourceType: 'Parameters',
    parameter: [
      { name: 'participantID', valueIdentifier: { system: 'http://cms.gov/fhir/access/identifier/participant', value: participantId || '' } },
      { name: 'payerID',       valueIdentifier: { system: 'http://cms.gov/fhir/access/identifier/payer',       value: payerId || '' } },
      {
        name: 'patient',
        resource: {
          resourceType: 'Patient',
          identifier: [{ type: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v2-0203', code: 'MC' }] }, system: 'http://hl7.org/fhir/sid/us-mbi', value: mbi || '' }],
          name: [{ family: lastName || '', given: [firstName || ''] }],
          birthDate: (dob || '').toString().slice(0, 10),
          gender: toFhirGender(sex),
        },
      },
      { name: 'track', valueCodeableConcept: { coding: [{ system: 'http://cms.gov/fhir/access/CodeSystem/ACCESSTrackCS', code: track.code, display: track.display }] } },
      { name: 'isProviderReferral', valueBoolean: !!isProviderReferral },
    ],
  };
  // `conditions` is STRICTLY the wire projection: vetted {icd10, display} pairs
  // (packet.payload). The raw-string arm is gone — an unverified value can no
  // longer reach the FHIR body (spec 2026-07-11 R9).
  for (const c of (Array.isArray(conditions) ? conditions : [])) {
    const code = (c && typeof c === 'object' && c.icd10) || '';
    const display = (c && typeof c === 'object' && c.display) || code;
    if (!code) continue;
    params.parameter.push({
      name: 'condition',
      resource: {
        resourceType: 'Condition',
        code: { coding: [{ system: 'http://hl7.org/fhir/sid/icd-10-cm', code, display }] },
        subject: { reference: 'Patient/' + (mbi || '') },
        clinicalStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-clinical', code: 'active' }] },
        verificationStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status', code: 'confirmed' }] },
        category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-category', code: 'problem-list-item' }] }],
      },
    });
  }
  // Attestation metadata rides the cms_alignment_requested payload and is stored
  // on care_period by WF12; it is not part of the FHIR $align body. Surface it on
  // the preview as a sibling note so the operator can verify it before submit.
  return {
    fhir_parameters: params,
    attestation_metadata: {
      reviewed_by: reviewedBy || null,
      reviewed_by_role: reviewedByRole || null,
      attestation_text: attestationText || null,
      attestation_signature: attestationSignature || null,
    },
  };
}

/* ── Tab bar ────────────────────────────────────────────────────── */
function TabBar({ active, onChange, warnings }) {
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid var(--wi-divider)', padding: '0 24px', gap: 0, flexWrap: 'wrap' }}>
      {TABS.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '12px 16px',
            fontSize: 13, fontWeight: active === t.id ? 600 : 400,
            color: active === t.id ? 'var(--wi-text-primary)' : 'var(--wi-text-secondary)',
            borderBottom: active === t.id ? '2px solid var(--wi-action-primary)' : '2px solid transparent',
            marginBottom: -1,
            whiteSpace: 'nowrap',
            fontFamily: 'var(--wi-font-family)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {t.label}
          {warnings?.[t.id] && (
            <span title={warnings[t.id].title} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: warnings[t.id].blocking ? 'var(--wi-status-bad)' : 'var(--wi-status-moderate)',
              flexShrink: 0,
            }} />
          )}
        </button>
      ))}
    </div>
  );
}

/* ── Inline chip editor for packet conditions — STRICT vocabulary ──
   `items` = the modal's string[] state (canonical labels / extended displays);
   `resolved` = packet.perCondition (parallel display rows). Entry is validated
   by validateConditionInput — unsupported input is rejected inline (role=alert),
   never committed; blur never commits (no silent adds). */
function ConditionChips({ items, resolved, onChange }) {
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);
  function handleAdd() {
    const verdict = validateConditionInput(input, items);
    if (!verdict.ok) { setError(verdict.error); return; }
    onChange([...items, verdict.value]);
    setInput(''); setError(null); setAdding(false);
  }
  function handleCancel() { setAdding(false); setInput(''); setError(null); }
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {items.map((item, i) => {
        const r = resolved?.[i];
        const label = r?.label ?? item;
        const bad = r ? !r.resolved : false;
        return (
        <span key={item} title={r?.display || undefined} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 6,
          background: bad ? 'var(--wi-status-bad-10)' : 'var(--wi-background-tertiary)',
          border: bad ? '1px solid var(--wi-status-bad)' : '1px solid transparent',
          color: 'var(--wi-text-primary)',
          fontSize: 13, fontFamily: 'var(--wi-font-family)',
        }}>
          <span>{label}</span>
          {r && (r.resolved
            ? (r.icd10 !== label && (
                <span className="detail1" style={{ color: 'var(--wi-text-secondary)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>{r.icd10}</span>
              ))
            : (
                <span className="detail1" style={{ color: 'var(--wi-status-bad)' }} title="Not in the ACCESS vocabulary — remove or replace before submitting">⚠ unsupported</span>
              ))}
          {r?.extended && (
            <span className="detail1" style={{ color: 'var(--wi-text-secondary)' }} title="CMS-only condition — sent to CMS, never on the care plan / Elation problem list">CMS-only</span>
          )}
          <button
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--wi-text-secondary)', padding: 0, lineHeight: 1 }}
            aria-label={`Remove ${label}`}
          >×</button>
        </span>
      ); })}
      {adding ? (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input
              autoFocus value={input} list="alignment-condition-options"
              aria-label="Condition or ICD-10 code"
              aria-describedby={error ? 'condition-add-error' : undefined}
              onChange={e => { setInput(e.target.value); if (error) setError(null); }}
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { e.stopPropagation(); handleCancel(); } }}
              placeholder="Condition or code, e.g. N18.30"
              style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${error ? 'var(--wi-status-bad)' : 'var(--wi-divider)'}`, fontSize: 13, width: 210, fontFamily: 'var(--wi-font-family)', outline: 'none' }}
            />
            <datalist id="alignment-condition-options">
              {conditionSuggestions().map((s) => <option key={s} value={s} />)}
            </datalist>
            <button onClick={handleAdd} className="detail1" style={{ background: 'none', border: '1px solid var(--wi-divider)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: 'var(--wi-text-primary)', fontFamily: 'var(--wi-font-family)' }}>Add</button>
            <button onClick={handleCancel} className="detail1" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)' }}>Cancel</button>
          </span>
          {error && (
            <span id="condition-add-error" role="alert" className="detail1" style={{ color: 'var(--wi-status-bad)', maxWidth: 340 }}>{error}</span>
          )}
        </span>
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{ background: 'none', border: '1px dashed var(--wi-divider)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)' }}
        >+ Add condition</button>
      )}
    </div>
  );
}

/* ── FhirKv: helper for the FHIR preview tab ─────────── */
function FhirKv({ rows }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 1fr) 2fr',
        rowGap: 6,
        columnGap: 12,
        padding: '10px 12px',
        background: 'var(--wi-background-primary)',
        borderRadius: 8,
        border: '1px solid var(--wi-divider)',
      }}
    >
      {rows.map(([label, value, opts], i) => (
        <React.Fragment key={i}>
          <span className="detail1" style={{ color: 'var(--wi-text-secondary)' }}>{label}</span>
          <span
            className="body2"
            style={{
              color: opts?.placeholder ? 'var(--wi-text-placeholder)' : 'var(--wi-text-primary)',
              fontFamily: opts?.mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : undefined,
              letterSpacing: opts?.mono ? '0.02em' : undefined,
              fontStyle: opts?.placeholder ? 'italic' : undefined,
              wordBreak: 'break-all',
            }}
          >
            {value}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── Data row helper ───────────────────────────────────────────── */
function Row({ label, value, warn }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: '1px solid var(--wi-divider)' }}>
      <span className="detail1 text-secondary">{label}</span>
      <span className="body2" style={{ fontWeight: 500, color: warn ? 'var(--wi-status-bad)' : 'var(--wi-text-primary)', textAlign: 'right' }}>
        {value || (warn ? 'Missing' : '—')}
      </span>
    </div>
  );
}

/* ── Warning banner ────────────────────────────────────────────── */
function Banner({ tone = 'amber', children }) {
  const BG = {
    amber: { bg: 'var(--wi-background-moderate)', border: 'var(--wi-status-moderate)', color: 'var(--wi-text-primary-moderate)' },
    red:   { bg: 'var(--wi-background-bad)',      border: 'var(--wi-status-bad)', color: 'var(--wi-status-bad)' },
    info:  { bg: 'var(--wi-background-info)',     border: 'var(--wi-status-info)', color: 'var(--wi-action-primary)' },
    good:  { bg: 'var(--wi-status-good-10)',      border: 'var(--wi-status-good)', color: 'var(--wi-status-good)' },
  }[tone];
  return (
    <div style={{
      background: BG.bg, border: `1px solid ${BG.border}`, color: BG.color,
      borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, lineHeight: 1.4,
    }}>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
export default function AlignmentPacketModal({
  patient,
  patientDetails: patientDetailsProp,
  carePlan,
  carePeriod,
  // Insurance / consent / eligibility come pre-fetched from the patient-file view
  // in PatientDetail — no lazy refetch inside the modal. `null` means "no record on file".
  insurance = /** @type {object | null} */ (null),
  consent = /** @type {object | null} */ (null),
  eligibility = /** @type {object | null} */ (null),
  currentUser,
  userRole,
  onClose,
  onSubmitted,
}) {
  const reviewerName = useMemo(
    () => (currentUser
      ? [currentUser.firstname, currentUser.lastname].filter(Boolean).join(' ')
      : ''),
    [currentUser]
  );

  const [activeTab, setActiveTab] = useState('demographics');
  const [showRawJson, setShowRawJson] = useState(false);
  // Reuse the details already fetched by PatientDetail — no re-fetch needed.
  const patientDetails = patientDetailsProp || null;
  const [conditions, setConditions] = useState(
    Array.isArray(carePlan?.conditions) ? [...carePlan.conditions] : []
  );
  const [attestationText, setAttestationText] = useState(DEFAULT_ATTESTATION);
  const [signature, setSignature] = useState('');
  const [providerReferral, setProviderReferral] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ── Fire-and-poll verdict state (parity with CmsEligibilityModal) ──────────
  // 'review'     → the 6 review tabs (default)
  // 'submitting' → event fired, polling cms_submissions(operation='align')
  // 'result'     → terminal verdict rendered
  const [phase, setPhase] = useState('review');
  const [result, setResult] = useState(null);
  const [resultVisible, setResultVisible] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // null until captured — gates the poll on (phase submitting AND captured).
  const [baselineIds, setBaselineIds] = useState(null);
  const aliveRef = useRef(true);
  const capTimerRef = useRef(null);
  const settledRef = useRef(false); // verdict OR cap resolved — keeps both paths idempotent

  const qc = useQueryClient();
  const triggerMut = useFireAlignment();
  const captureBaseline = useCaptureCmsBaseline('align');
  const uid = String(patient.userid ?? '');
  // Eligibility state without the never-written apiv2 view field (claims_updated_at):
  // the check_eligibility submission is the direct verdict, care_period.status the fallback.
  const allCmsSubmissions = useCmsSubmissions(uid);
  // Provider-referral pre-fill: seed the toggle from the enrollment intake's
  // referred_by_provider (intake_type='onboarding'); operator-overridable.
  const onboardingIntakes = usePatientIntakes(uid, { intakeType: 'onboarding' });
  const poll = useCmsSubmissionPoll('align', uid, {
    enabled: phase === 'submitting' && !!baselineIds,
    baselineIds: baselineIds || EMPTY_IDS,
  });

  const inFlight = phase === 'submitting';

  // Sync conditions seed when carePlan loads/changes
  useEffect(() => {
    if (Array.isArray(carePlan?.conditions)) setConditions([...carePlan.conditions]);
  }, [carePlan]);

  // Seed the provider-referral toggle from the onboarding intake once it loads.
  useEffect(() => {
    const rows = onboardingIntakes.data?.data;
    const val = Array.isArray(rows) && rows[0]?.data?.referred_by_provider;
    if (val != null && val !== false) setProviderReferral(String(val).toLowerCase() === 'yes');
  }, [onboardingIntakes.data]);

  // Derived data
  const member = patientDetails?.member || {};
  // ACCESS is Medicare-only: insurance_info.member_id holds the MBI.
  // policy_number is reserved for future commercial payers and is ignored here.
  const mbi = insurance?.member_id || null;
  const insuranceProvider = insurance?.insurance_provider || null;
  // Withings birthdate is epoch-seconds — normalize to YYYY-MM-DD for CMS/WF12 + display.
  const dob = toIsoBirthdate(member.birthdate || patient.birthdate) || null;
  const sex = member.gender || patient.gender || null;

  // The packet — derived FRESH from the live condition list + the plan's chosen
  // subtrack (combination codes), NEVER from the stored (stale-able) bundle.
  // packet.payload is the exact wire projection sent to CMS; unresolvedCount > 0
  // BLOCKS submit (strict posture — no send-as-is).
  const packet = useMemo(
    () => buildAlignmentPacket(conditions, carePlan),
    [conditions, carePlan]
  );
  const unresolvedCount = packet.unresolvedCount;

  // Validation
  const planConditions = Array.isArray(carePlan?.conditions) ? carePlan.conditions : [];
  const planPacket = useMemo(
    () => buildAlignmentPacket(Array.isArray(carePlan?.conditions) ? carePlan.conditions : [], carePlan),
    [carePlan]
  );
  const conditionsChanged =
    conditions.length !== planConditions.length
    || conditions.some(c => !planConditions.includes(c));

  const missingMbi         = !mbi;
  const missingConsent     = !consent;
  const missingConditions  = conditions.length === 0;

  const eligSubmission = (allCmsSubmissions.data || []).find(r => r.operation === 'check_eligibility') || null;
  const eligView = deriveEligibility(carePeriod?.status, eligSubmission);
  // Stale only when we know the check date AND it's >30d; unknown date (status-only) ≠ stale.
  const eligStale = eligView.checkedAt
    ? (Date.now() - new Date(eligView.checkedAt).getTime()) > 30 * 24 * 3600 * 1000
    : false;

  const signatureValid =
    signature.trim().length > 0
    && reviewerName.trim().toLowerCase() === signature.trim().toLowerCase();

  // emptyPayload: chips can all be green yet produce ZERO CMS codes (no-subtrack
  // legacy plan, e.g. Dyslipidemia alone) — $align requires 1..* conditions.
  const emptyPayload = packet.payload.length === 0;
  const canSubmit =
    !missingMbi && !missingConsent && !missingConditions
    && unresolvedCount === 0 && !emptyPayload
    && signatureValid && attestationText.trim().length > 10
    && !inFlight;

  // Per-tab warnings for the TabBar dots
  const warnings = {
    demographics: missingMbi      ? { blocking: true,  title: 'MBI missing' } : null,
    conditions:   missingConditions ? { blocking: true, title: 'At least one ICD-10 required' }
                : unresolvedCount > 0 ? { blocking: true, title: 'Unsupported condition on the packet' }
                : emptyPayload ? { blocking: true, title: 'No CMS-submittable code set' }
                : null,
    consent:      missingConsent  ? { blocking: true,  title: 'No active consent on file' } : null,
    eligibility:  eligView.needsCheck ? { blocking: false, title: 'No eligibility check on file' }
                : eligView.state === 'ineligible'  ? { blocking: true,  title: 'CMS returned not-eligible — review before submitting' }
                : eligStale                        ? { blocking: false, title: 'Eligibility check older than 30 days' }
                : null,
    attestation:  (!signatureValid || !attestationText.trim()) ? { blocking: true, title: 'Attestation incomplete' } : null,
  };

  // Build the verdict `result` the render expects from a terminal poll row.
  const rowToResult = useCallback((row) => ({
    result_code: row.result_code,
    display: row.result_display,
    reason: row.result_display,
    checked_at: row.finished_at || row.created || row.created_at || null,
  }), []);

  function handleSubmit() {
    if (!canSubmit) return;
    setSubmitError(null);
    setStepIndex(0);
    setResult(null);
    setResultVisible(false);
    settledRef.current = false;
    setPhase('submitting');
    (async () => {
      // Snapshot existing align rows BEFORE firing — skew-immune freshness.
      const baseline = await captureBaseline(uid);
      if (!aliveRef.current) return;
      setBaselineIds(baseline); // enables the poll (gated on phase + captured)
      if (capTimerRef.current) clearTimeout(capTimerRef.current);
      capTimerRef.current = setTimeout(() => {
        // Fail-soft: CMS slower than the cap → pending ("care team will follow up").
        if (!aliveRef.current || settledRef.current) return;
        settledRef.current = true;
        setResult({ result_code: 'pending' });
        setPhase('result');
        requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
      }, POLL_CAP_MS);
      // Fire; the verdict comes from the poll, not this mutation's response.
      // App = source of truth: full FHIR payload + signed attestation → WF12.
      triggerMut.mutate(
        {
          withingsUserId: patient.userid,
          carePeriodId: carePeriod?.care_period_id,
          mbi: mbi || '',
          firstName: patient.firstname || '',
          lastName: patient.lastname || '',
          dateOfBirth: (dob || '').toString().slice(0, 10),
          gender: toFhirGender(sex),
          trackCode: carePeriod?.track_code || 'eckm',
          isProviderReferral: providerReferral,
          conditions: packet.payload, // vetted wire projection (cms view) — stored = sent
          reviewedBy: reviewerName,
          reviewedByRole: userRole,
          attestationText: attestationText.trim(),
          attestationSignature: signature.trim(),
        },
        {
          onError: (e) => {
            // The fire itself failed (network/transport) — back to review.
            if (!aliveRef.current || settledRef.current) return;
            settledRef.current = true;
            if (capTimerRef.current) clearTimeout(capTimerRef.current);
            setSubmitError(e.message || 'Alignment failed');
            setPhase('review');
          },
        },
      );
    })();
  }

  // Verdict from the poll: the first FRESH row (correlation_id not in baselineIds)
  // that has reached a terminal verdict. Same predicate the hook's refetchInterval uses.
  useEffect(() => {
    if (phase !== 'submitting' || !baselineIds) return;
    const fresh = freshTerminalRow(poll.data, baselineIds, 'align');
    if (!fresh || settledRef.current) return;
    settledRef.current = true;
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    setResult(rowToResult(fresh));
    setPhase('result');
    requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
    // WF12 after-success cascade (activate care period, create post-alignment tasks)
    // land out-of-band — reconcile past the SLA.
    scheduleInvalidate(qc, TASK_KEYS.all);
    scheduleInvalidate(qc, CMS_KEYS.all);
    scheduleInvalidate(qc, CARE_PERIOD_KEYS.byUser(uid));
  }, [phase, baselineIds, poll.data, rowToResult, qc, uid]);

  // Loading-step animation
  useEffect(() => {
    if (phase !== 'submitting') return undefined;
    const timers = SUBMIT_LOADING_STEPS.map((s, i) =>
      setTimeout(() => { if (aliveRef.current) setStepIndex(i); }, s.at)
    );
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  // Dedicated mount/unmount cleanup — this modal has no mount-fire effect to host
  // it, so the cap timer + aliveRef live here (a modal closed mid-submit must not
  // setState on an unmounted tree).
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (capTimerRef.current) clearTimeout(capTimerRef.current);
    };
  }, []);

  // Body scroll lock for the modal's lifetime.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Escape closes — gated off mid-submit so a stray key can't dismiss the in-flight wait.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !inFlight) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, inFlight]);

  function handleBackToReview() {
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    settledRef.current = false;
    setBaselineIds(null);
    setResult(null);
    setResultVisible(false);
    setSubmitError(null);
    setPhase('review');
  }

  function handleDone() {
    onSubmitted?.(); // shell closes + refetches care period (now active) + tasks
  }

  /* ── Verdict derived ─────────────────────────────────────────── */
  const resultMeta = result
    ? CmsResultCodes.labelFor(result.result_code) || {
        headline: result.display || 'Alignment result',
        explainer: '',
        tone: 'moderate',
      }
    : null;
  const isAligned = result ? CmsResultCodes.isSuccess(result.result_code) : false;
  const isPendingish = result ? ['pending', 'alignment-pending'].includes(result.result_code) : false;
  const isFixable = result ? FIXABLE_ALIGN_CODES.has(result.result_code) : false;

  /* ── Tab panels ─────────────────────────────────────────────── */

  function DemographicsPanel() {
    // Give the reviewer a specific reason — empty row vs. missing field.
    let mbiDiagnostic = null;
    if (!mbi) {
      if (!insurance) {
        mbiDiagnostic = (
          <>
            <b>No insurance record on file.</b> The patient was onboarded without a Medicare insurance entry,
            or the <code>insurance_info</code> row was not created. Add it via the Platform API or re-run onboarding
            with <code>insurances[0].member_id</code> populated.
          </>
        );
      } else {
        mbiDiagnostic = (
          <>
            <b>Medicare MBI is missing.</b> An insurance record exists
            {insuranceProvider ? <> for <b>{insuranceProvider}</b></> : null}
            {' '}but <code>member_id</code> is empty. Update the patient&apos;s insurance record with the
            11-character MBI from their Medicare card and retry.
          </>
        );
      }
    }
    return (
      <div>
        {mbiDiagnostic && <Banner tone="red">{mbiDiagnostic}</Banner>}
        <Row label="Name"        value={`${patient.firstname || ''} ${patient.lastname || ''}`.trim()} />
        <Row label="Date of birth" value={dob ? new Date(dob).toLocaleDateString() : null} warn={!dob} />
        <Row label="Sex"         value={sex} />
        <Row label="Insurance provider" value={insuranceProvider} />
        <Row label="Medicare MBI" value={mbi} warn={!mbi} />
        <Row label="Phone"       value={member.phone_number || patient.phone_number} />
        <Row label="Elation ID"  value={patient.ehr_patient_id || member.ehr_patient_id} />
        <Row label="Withings user ID" value={patient.userid} />
        <Row label="Track code"  value={carePeriod?.track_code || 'eckm'} />
      </div>
    );
  }

  function ConditionsPanel() {
    return (
      <div>
        <p className="body2 text-secondary" style={{ margin: '0 0 10px' }}>
          Conditions sent in the FHIR <code>$align</code> Parameters, with the ICD-10 code resolved from the care plan. Edits here override the care plan for this submission only.
        </p>
        {missingConditions && (
          <Banner tone="red">At least one qualifying ICD-10 condition is required.</Banner>
        )}
        <ConditionChips items={conditions} resolved={packet.perCondition} onChange={setConditions} />
        {unresolvedCount > 0 && (
          <Banner tone="red">
            <b>{unresolvedCount} condition{unresolvedCount === 1 ? '' : 's'} not in the ACCESS vocabulary</b> — submission is blocked. Remove or replace the flagged chip{unresolvedCount === 1 ? '' : 's'} (pick from the list, or ask to add the condition to the vocabulary). Nothing is ever sent to CMS as an unverified code.
          </Banner>
        )}
        {!missingConditions && unresolvedCount === 0 && emptyPayload && (
          <Banner tone="red">
            <b>These conditions do not form a CMS-submittable set</b> — no ACCESS subtrack matches them, so the packet would carry zero ICD-10 codes and CMS <code>$align</code> would reject it. Review the qualifying conditions (or the care-plan pathway) before aligning.
          </Banner>
        )}
        {conditionsChanged && (
          <Banner tone="info">
            <b>Override active.</b> {conditions.length} condition{conditions.length === 1 ? '' : 's'} will be sent to CMS instead of the {planConditions.length} on the care plan.
          </Banner>
        )}
        {packet.cmsView.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--wi-divider)' }}>
            <span className="detail1 text-secondary">Sent to CMS ({packet.cmsView.length} code{packet.cmsView.length === 1 ? '' : 's'}{packet.subtrackId ? ` · subtrack ${packet.subtrackId}` : ''})</span>
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {packet.cmsView.map((c, i) => (
                <span key={i} className="detail1" style={{ color: 'var(--wi-text-primary)' }}>
                  <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>{c.icd10}</span>
                  {' — '}{c.display}
                  {c.extended && <span style={{ color: 'var(--wi-text-secondary)' }}> · CMS-only extra</span>}
                </span>
              ))}
            </div>
            <p className="detail1 text-secondary" style={{ margin: '8px 0 0' }}>
              The CMS view uses ACCESS combination codes where conditions are clinically linked (e.g. E11.22 for diabetes with CKD) — it can differ from the one-code-per-condition chips above.
            </p>
          </div>
        )}
        {planConditions.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--wi-divider)' }}>
            <span className="detail1 text-secondary">Care plan baseline</span>
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {planPacket.perCondition.map((c, i) => (
                <span key={i} style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-secondary)' }}>
                  {c.label}{c.icd10 ? ` · ${c.icd10}` : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  function ConsentPanel() {
    if (!consent) {
      return (
        <Banner tone="red">
          <b>No active consent record.</b> Capture written or electronic consent before submitting CMS alignment.
        </Banner>
      );
    }
    return (
      <div>
        <Banner tone="info">Consent is on file. CMS will receive attestation that the patient consented to the ACCESS eCKM program.</Banner>
        <Row label="Status"           value={consent.status} />
        <Row label="Version"          value={consent.consent_version} />
        <Row label="Signed by"        value={consent.signer_typed_name} />
        <Row label="Signed at"        value={consent.signed_at ? new Date(consent.signed_at).toLocaleString() : null} />
        <Row label="Document SHA-256" value={consent.consent_text_sha256 ? consent.consent_text_sha256.slice(0, 16) + '…' : null} />
        <Row label="Elation document" value={consent.elation_document_id || null} />
        <Row label="Signer IP"        value={consent.signer_ip} />
      </div>
    );
  }

  function EligibilityPanel() {
    if (eligView.needsCheck) {
      return (
        <Banner tone="amber">
          No recorded <code>$check-eligibility</code> result. This does not block submission but CMS may reject the alignment if the patient is not eligible.
        </Banner>
      );
    }
    const { state, checkedAt, resultDisplay } = eligView;
    const verdictTone = state === 'eligible' ? 'good' : state === 'ineligible' ? 'red' : 'info';
    const verdictText = state === 'eligible' ? 'Eligible' : state === 'ineligible' ? 'Not eligible' : 'Eligibility recorded';
    // The apiv2 view still supplies task-based detail (status/claims/summary) when present.
    const elig = eligibility || {};
    const claims = elig.claims || { diagnoses: [], medications: [], labs: [] };
    return (
      <div>
        <Banner tone={verdictTone}>{resultDisplay || verdictText}</Banner>
        {eligStale && (
          <Banner tone="amber">Last eligibility check was more than 30 days ago. Consider re-running before submitting.</Banner>
        )}
        {checkedAt && <Row label="Last check" value={new Date(checkedAt).toLocaleString()} />}
        {elig.taskStatus && <Row label="Eligibility task status" value={elig.taskStatus} />}
        <Row label="CMS claims diagnoses"   value={`${claims.diagnoses.length} rows`} />
        <Row label="CMS claims medications" value={`${claims.medications.length} rows`} />
        <Row label="CMS claims labs"        value={`${claims.labs.length} rows`} />
        {elig.summary && (
          <div style={{ marginTop: 14 }}>
            <span className="detail1 text-secondary">Summary</span>
            <pre style={{
              whiteSpace: 'pre-wrap', margin: '6px 0 0', padding: 12,
              background: 'var(--wi-background-tertiary)', borderRadius: 8,
              fontSize: 12, fontFamily: 'ui-monospace, monospace', lineHeight: 1.5,
            }}>{elig.summary}</pre>
          </div>
        )}
      </div>
    );
  }

  function AttestationPanel() {
    return (
      <div>
        <Row label="Reviewer"      value={reviewerName || '— not signed in'} warn={!reviewerName} />
        <Row label="Role"          value={userRole} />
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <input
            type="checkbox"
            id="provider-referral"
            checked={providerReferral}
            onChange={e => setProviderReferral(e.target.checked)}
            style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--wi-action-primary)' }}
          />
          <label htmlFor="provider-referral" className="body2" style={{ color: 'var(--wi-text-primary)' }}>
            Provider referral — patient was referred by their provider{' '}
            <span className="detail1 text-secondary">(pre-filled from enrollment intake; sent to CMS $align as isProviderReferral)</span>
          </label>
        </div>
        <div style={{ marginTop: 18 }}>
          <label className="detail1 text-secondary" style={{ display: 'block', marginBottom: 6 }}>
            Attestation statement
          </label>
          <textarea
            value={attestationText}
            onChange={e => setAttestationText(e.target.value)}
            rows={4}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: '1px solid var(--wi-divider)',
              background: 'var(--wi-background-primary)',
              color: 'var(--wi-text-primary)',
              fontSize: 13, lineHeight: 1.5,
              fontFamily: 'var(--wi-font-family)', resize: 'vertical', outline: 'none',
            }}
          />
        </div>
        <div style={{ marginTop: 14 }}>
          <label className="detail1 text-secondary" style={{ display: 'block', marginBottom: 6 }}>
            Sign by typing your full name ({reviewerName || 'not signed in'})
          </label>
          <input
            value={signature}
            onChange={e => setSignature(e.target.value)}
            placeholder={reviewerName}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: `1px solid ${signature && !signatureValid ? 'var(--wi-status-bad)' : 'var(--wi-divider)'}`,
              background: 'var(--wi-background-primary)',
              color: 'var(--wi-text-primary)',
              fontSize: 14,
              fontFamily: 'Caveat, cursive, var(--wi-font-family)', outline: 'none',
            }}
          />
          {signature && !signatureValid && (
            <p className="detail1" style={{ color: 'var(--wi-status-bad)', margin: '6px 0 0' }}>
              Signature must match your account name exactly.
            </p>
          )}
        </div>
        <p className="detail1 text-secondary" style={{ margin: '16px 0 0', lineHeight: 1.5 }}>
          By signing, <b>{reviewerName || 'you'}</b> assumes responsibility for the accuracy of this CMS alignment
          submission. Your name, role, the attestation text above, and a frozen copy of the FHIR payload sent
          to CMS will be stored on the care period for audit.
        </p>
      </div>
    );
  }

  function FhirPreviewPanel() {
    const { fhir_parameters: fhirParams, attestation_metadata: attestation } = buildAlignmentFhirParameters({
      mbi,
      firstName: patient.firstname,
      lastName: patient.lastname,
      dob,
      sex,
      // participant + payer ids are server-side $vars (n8n) — surface them as
      // placeholders so the operator knows they'll be filled at submit time.
      participantId: '<set by n8n $vars.ACCESS_PARTICIPANT_ID>',
      payerId: '<set by n8n $vars.CMS_PAYER_ID>',
      trackCode: carePeriod?.track_code,
      conditions: packet.payload, // vetted wire projection (cms view) — stored = sent
      isProviderReferral: providerReferral,
      reviewedBy: reviewerName,
      reviewedByRole: userRole,
      attestationText,
      attestationSignature: signature,
    });

    const trackKey = (carePeriod?.track_code || 'eckm').toLowerCase();
    const track = TRACK_INFO[trackKey] || TRACK_INFO.eckm;
    const fullName = `${patient.firstname || ''} ${patient.lastname || ''}`.trim();
    const attestExcerpt = (attestation.attestation_text || '').slice(0, 120) + ((attestation.attestation_text || '').length > 120 ? '…' : '');

    return (
      <div>
        <div
          style={{
            background: 'var(--wi-background-info)',
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 18,
          }}
        >
          <h3 className="header4" style={{ margin: '0 0 6px', color: 'var(--wi-text-primary-info)' }}>
            FHIR <code>$align</code> payload — final preview
          </h3>
          <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-info)', lineHeight: 1.45 }}>
            This is exactly what WF12 will POST to <code>/Patient/$align</code> when you click <strong>Submit alignment</strong> on the Attestation tab. Edits made on the Conditions tab are reflected here live.
          </p>
        </div>

        <Section label="Patient identity">
          <FhirKv rows={[
            ['Medicare MBI', mbi || '—', { mono: true }],
            ['Name', fullName || '—'],
            ['Date of birth', (dob || '').toString().slice(0, 10) || '—'],
            ['Sex', sex || '—'],
          ]} />
        </Section>

        <Section label="Track + participant">
          <FhirKv rows={[
            ['ACCESS track', `${track.code} — ${track.display}`],
            ['Participant ID', '<set by n8n $vars.ACCESS_PARTICIPANT_ID>', { mono: true, placeholder: true }],
            ['Payer ID', '<set by n8n $vars.CMS_PAYER_ID>', { mono: true, placeholder: true }],
            ['isProviderReferral', String(providerReferral)],
          ]} />
        </Section>

        <Section label={`Conditions sent (${packet.payload.length})`}>
          {packet.payload.length === 0 ? (
            <p className="body2" style={{ margin: 0, color: 'var(--wi-status-bad)' }}>
              No conditions — CMS $align requires at least one Condition resource. Add conditions on the Conditions tab.
            </p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {packet.cmsView.map((c, i) => (
                <span
                  key={i}
                  className="detail1"
                  title={c.display}
                  style={{
                    padding: '3px 10px',
                    borderRadius: 999,
                    background: 'var(--wi-background-secondary)',
                    color: 'var(--wi-text-secondary)',
                    border: '1px solid var(--wi-divider)',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  }}
                >
                  {c.icd10}{c.extended ? ' · CMS-only' : ''}
                </span>
              ))}
            </div>
          )}
        </Section>

        <Section label="Reviewer attestation (audit trail, not part of FHIR body)">
          <FhirKv rows={[
            ['Reviewer', attestation.reviewed_by || '— not signed in'],
            ['Role', attestation.reviewed_by_role || '—'],
            ['Statement', attestExcerpt || '—'],
            ['Signature', attestation.attestation_signature || '— not signed'],
          ]} />
        </Section>

        <Section label="Raw FHIR Parameters">
          <button
            type="button"
            onClick={() => setShowRawJson(v => !v)}
            aria-expanded={showRawJson}
            aria-controls="alignment-fhir-json"
            className="detail1"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: '1px solid var(--wi-divider)',
              cursor: 'pointer', padding: '4px 10px', borderRadius: 6,
              color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)',
              marginBottom: showRawJson ? 8 : 0,
            }}
          >
            {showRawJson ? 'Hide raw JSON' : 'Show raw JSON'}
          </button>
          {showRawJson && (
            <pre
              id="alignment-fhir-json"
              style={{
                margin: 0, padding: 12,
                background: 'var(--wi-background-primary)',
                border: '1px solid var(--wi-divider)',
                borderRadius: 8,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: 11, lineHeight: 1.4,
                color: 'var(--wi-text-primary)',
                overflowX: 'auto', maxHeight: 320, overflowY: 'auto',
                whiteSpace: 'pre',
              }}
            >
              {JSON.stringify(fhirParams, null, 2)}
            </pre>
          )}
        </Section>
      </div>
    );
  }

  /* ── Render ──────────────────────────────────────────────────── */

  return createPortal((
    <div className="modal-overlay" onClick={() => { if (!inFlight) onClose(); }}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 640, width: '100%', padding: 0,
          display: 'flex', flexDirection: 'column', maxHeight: '88vh',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 24px 12px',
          borderBottom: phase === 'review' ? 'none' : '1px solid var(--wi-divider)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h3 className="header4" style={{ margin: 0 }}>CMS Alignment Packet</h3>
              <p className="detail1 text-secondary" style={{ margin: '2px 0 0' }}>
                {phase === 'review'
                  ? `Review the payload that will be sent to CMS $align for ${patient.firstname} ${patient.lastname}.`
                  : `Medicare $align · ACCESS Model · ${patient.firstname} ${patient.lastname}`}
              </p>
            </div>
            <button
              className="modal-close"
              onClick={onClose}
              disabled={inFlight}
              aria-label="Close"
              style={{ position: 'static' }}
            >×</button>
          </div>
        </div>

        {/* Tabs — review phase only */}
        {phase === 'review' && (
          <TabBar active={activeTab} onChange={setActiveTab} warnings={warnings} />
        )}

        {/* Body */}
        {/* NOTE: review-phase panels are called as functions, not rendered as
            <Component />. They close over parent state, so rendering them as JSX
            elements would make React treat them as a new component type on every
            keystroke, unmounting the <input> and stealing focus mid-typing. */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {phase === 'review' && (
            <>
              {activeTab === 'demographics' && DemographicsPanel()}
              {activeTab === 'conditions'   && ConditionsPanel()}
              {activeTab === 'consent'      && ConsentPanel()}
              {activeTab === 'eligibility'  && EligibilityPanel()}
              {activeTab === 'attestation'  && AttestationPanel()}
              {activeTab === 'fhir-preview' && FhirPreviewPanel()}
            </>
          )}

          {phase === 'submitting' && (
            <LoadingPanel
              title="Submitting alignment to CMS…"
              stepIndex={stepIndex}
              steps={SUBMIT_LOADING_STEPS.map(s => s.label)}
              footnote="CMS may take up to 30 seconds to respond."
            />
          )}

          {phase === 'result' && result && resultMeta && (
            <div style={{ opacity: resultVisible ? 1 : 0, transition: 'opacity 200ms ease' }}>
              <VerdictBanner
                tone={resultMeta.tone}
                headline={resultMeta.headline}
                resultCode={result.result_code}
                fhirDisplay={result.display}
                explainer={resultMeta.explainer}
              />
              {isAligned && (
                <Callout tone="good">
                  <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-good)' }}>
                    The care period is being activated and post-alignment tasks are being created. Click Done to refresh.
                  </p>
                </Callout>
              )}
              {result.checked_at && (
                <p className="detail1" style={{ margin: '20px 0 0', color: 'var(--wi-text-placeholder)' }}>
                  Recorded {new Date(result.checked_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid var(--wi-divider)',
          padding: '14px 24px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          {phase === 'review' && (
            <>
              <div className="detail1 text-secondary" style={{ flex: 1, minWidth: 200 }}>
                {!canSubmit && (
                  <>
                    {missingMbi      && <div>• MBI required</div>}
                    {missingConsent  && <div>• Active consent required</div>}
                    {missingConditions && <div>• At least one ICD-10 required</div>}
                    {unresolvedCount > 0 && <div>• Unsupported condition on the packet — remove or replace it</div>}
                    {!missingConditions && unresolvedCount === 0 && emptyPayload && <div>• Conditions do not form a CMS-submittable code set</div>}
                    {(!signatureValid || !attestationText.trim()) && <div>• Signed attestation required</div>}
                  </>
                )}
                {submitError && (
                  <div style={{ color: 'var(--wi-status-bad)' }}>{submitError}</div>
                )}
              </div>
              <button className="btn-secondary" onClick={onClose} style={{ fontSize: 13, padding: '8px 16px' }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{ fontSize: 13, padding: '8px 20px' }}
              >
                Submit alignment
              </button>
            </>
          )}

          {phase === 'submitting' && (
            <div style={{ marginLeft: 'auto' }}>
              <button className="btn-secondary" onClick={onClose} style={{ fontSize: 13, padding: '8px 16px' }}>
                Cancel
              </button>
            </div>
          )}

          {phase === 'result' && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
              {(isAligned || isPendingish) && (
                <button className="btn-primary" onClick={handleDone} style={{ fontSize: 13, padding: '8px 20px' }}>
                  Done
                </button>
              )}
              {!isAligned && !isPendingish && isFixable && (
                <>
                  <button className="btn-secondary" onClick={onClose} style={{ fontSize: 13, padding: '8px 16px' }}>
                    Close
                  </button>
                  <button className="btn-primary" onClick={handleBackToReview} style={{ fontSize: 13, padding: '8px 20px' }}>
                    Back to review
                  </button>
                </>
              )}
              {!isAligned && !isPendingish && !isFixable && (
                <button className="btn-primary" onClick={onClose} style={{ fontSize: 13, padding: '8px 20px' }}>
                  Close
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}
